"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[var(--paper-radius-control)] border text-sm font-medium outline-none ring-ring/15 transition-[background-color,border-color,color,box-shadow,transform] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)] active:translate-y-px data-pressed:translate-y-px pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:border-ring focus-visible:ring-2 disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-50 data-loading:select-none data-loading:text-transparent [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:-mx-0.5 [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-9 px-3",
        icon: "size-9",
        "icon-lg": "size-10",
        "icon-sm": "size-8",
        "icon-xl": "size-10 [&_svg:not([class*='size-'])]:size-4.5",
        "icon-xs":
          "size-6 rounded-[var(--paper-radius-control-compact)] not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 px-3.5",
        sm: "h-8 gap-1.5 px-2.5",
        xl: "h-10 px-4 text-base [&_svg:not([class*='size-'])]:size-4.5",
        xs: "h-7 gap-1 rounded-md px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
      },
      variant: {
        default:
          "border-primary bg-primary text-primary-foreground hover:border-brand-10 hover:bg-brand-10 active:border-brand-10 active:bg-brand-10 data-pressed:border-brand-10 data-pressed:bg-brand-10 *:data-[slot=button-loading-indicator]:text-primary-foreground",
        destructive:
          "border-destructive bg-destructive text-white hover:bg-destructive/88 active:bg-destructive/80 data-pressed:bg-destructive/80 *:data-[slot=button-loading-indicator]:text-white",
        "destructive-outline":
          "border-destructive/40 bg-popover text-destructive-foreground hover:border-destructive hover:bg-destructive/5 active:border-destructive active:bg-destructive/10 data-pressed:border-destructive data-pressed:bg-destructive/10 *:data-[slot=button-loading-indicator]:text-destructive-foreground",
        ghost:
          "border-transparent text-foreground hover:bg-accent active:bg-border data-pressed:bg-border *:data-[slot=button-loading-indicator]:text-foreground",
        link: "border-transparent text-foreground underline-offset-4 hover:underline active:text-muted-foreground active:underline data-pressed:text-muted-foreground data-pressed:underline *:data-[slot=button-loading-indicator]:text-foreground",
        outline:
          "border-control-border bg-control-default text-foreground hover:bg-control-highlight active:border-muted-foreground/40 active:bg-border data-pressed:border-muted-foreground/40 data-pressed:bg-border aria-expanded:bg-control-highlight *:data-[slot=button-loading-indicator]:text-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/65 data-pressed:bg-secondary/65 *:data-[slot=button-loading-indicator]:text-secondary-foreground",
        surface:
          "border-[var(--paper-surface-control-border)] bg-surface text-ink hover:bg-subtle active:bg-control-hover data-pressed:bg-control-hover *:data-[slot=button-loading-indicator]:text-ink",
      },
    },
  },
);

export interface ButtonProps extends useRender.ComponentProps<"button"> {
  variant?: VariantProps<typeof buttonVariants>["variant"];
  size?: VariantProps<typeof buttonVariants>["size"];
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, render, children, loading = false, disabled: disabledProp, ...props },
  ref,
): React.ReactElement {
  const isDisabled: boolean = Boolean(loading || disabledProp);
  const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] = render
    ? undefined
    : "button";

  const defaultProps = {
    children: (
      <>
        {children}
        {loading && (
          <Spinner className="pointer-events-none absolute" data-slot="button-loading-indicator" />
        )}
      </>
    ),
    className: cn(buttonVariants({ className, size, variant })),
    "aria-disabled": loading || undefined,
    "data-loading": loading ? "" : undefined,
    "data-slot": "button",
    disabled: isDisabled,
    type: typeValue,
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps<"button">(defaultProps, props),
    render,
    ref,
  });
});
